import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '../lib/api.js';

type AcceptedSpec = { id: string; spec: Record<string, unknown>; resourceType: string; sourceSystem: string };

/**
 * The recipe reuse/authoring bar atop the FHIR import panel — the flat-file
 * analog of the scrape-side recipe banner. It fingerprints the active file's
 * headers and, for the session's source EHR, either:
 *  - REUSE: an accepted recipe matches → apply it deterministically (zero AI), or
 *  - NEW SHAPE: no match → author below, then save the accepted specs as a recipe
 *    (so the next matching drop reuses it). Drift (near-miss) is surfaced too.
 * If no EHR is attached yet, offers a one-field attach (the recipe anchor).
 */
export function RecipeReuseBar({
  sessionId,
  ehrId,
  activeUploadId,
  activeHeaders,
  acceptedSpecs,
  onApplyRecipe,
}: {
  sessionId: string;
  ehrId: string | null;
  activeUploadId: string | null;
  activeHeaders: string[];
  acceptedSpecs: AcceptedSpec[];
  onApplyRecipe: (specIds: string[]) => void;
}) {
  const qc = useQueryClient();
  const [slug, setSlug] = useState('');

  const hasHeaders = activeHeaders.length > 0;
  const lookupKey = ['recipe-lookup', sessionId, ehrId, activeHeaders.join('|')];
  const lookup = useQuery({
    queryKey: lookupKey,
    queryFn: () => api.lookupFlatFileRecipe(sessionId, { headers: activeHeaders }),
    enabled: Boolean(ehrId) && hasHeaders,
    staleTime: 10_000,
  });

  // Pre-launch deterministic health check on a reuse hit (design §4) — the verdict
  // the operator sees before pulling the trigger. Runs against the active file.
  const matchedRecipeId = lookup.data?.reuse ? lookup.data.recipe?.id : undefined;
  const health = useQuery({
    queryKey: ['recipe-health', sessionId, matchedRecipeId, activeUploadId],
    queryFn: () => api.healthCheckRecipe(sessionId, { uploadId: activeUploadId!, recipeId: matchedRecipeId! }),
    enabled: Boolean(matchedRecipeId) && Boolean(activeUploadId),
    staleTime: 10_000,
  });

  const attach = useMutation({
    mutationFn: () => api.attachSessionEhr(sessionId, { slug: slug.trim(), displayName: slug.trim() }),
    onSuccess: () => {
      toast.success('Source EHR attached');
      void qc.invalidateQueries({ queryKey: ['session-status', sessionId] });
      void qc.invalidateQueries({ queryKey: lookupKey });
    },
    onError: (e) => toast.error(`Attach failed: ${e instanceof Error ? e.message : String(e)}`),
  });

  const apply = useMutation({
    mutationFn: async (): Promise<string[]> => {
      const specs = lookup.data?.recipe?.specs ?? [];
      if (specs.length === 0) throw new Error('recipe carries no specs');
      const ids: string[] = [];
      for (const spec of specs) {
        const { specId } = await api.createFhirMappingSpec(sessionId, spec, {
          status: 'accepted',
          authoredBy: 'human',
          rationale: 'reused recipe (deterministic, no AI)',
        });
        ids.push(specId);
      }
      return ids;
    },
    onSuccess: (ids) => {
      onApplyRecipe(ids);
      void qc.invalidateQueries({ queryKey: ['fhir-specs', sessionId] });
      toast.success(`Applied recipe → ${ids.length} spec${ids.length !== 1 ? 's' : ''} (no AI)`);
    },
    onError: (e) => toast.error(`Apply failed: ${e instanceof Error ? e.message : String(e)}`),
  });

  // Re-scope: repair a failing matched recipe from the current file → new active version.
  const rescope = useMutation({
    mutationFn: () =>
      api.rescopeFlatFileRecipe(sessionId, {
        uploadId: activeUploadId!,
        sourceSystem: lookup.data?.sourceSystem,
        label: 'Re-scoped from this file',
      }),
    onSuccess: (res) => {
      toast.success(`Re-scoped → v${res.version}${res.health ? ` · health ${res.health.verdict}` : ''}`);
      void qc.invalidateQueries({ queryKey: lookupKey });
      void qc.invalidateQueries({ queryKey: ['recipe-health', sessionId] });
      if (ehrId) void qc.invalidateQueries({ queryKey: ['flat-file-recipes', ehrId] });
    },
    onError: (e) => toast.error(`Re-scope failed: ${e instanceof Error ? e.message : String(e)}`),
  });

  const save = useMutation({
    mutationFn: () => {
      const rtypes = [...new Set(acceptedSpecs.map((s) => s.resourceType))];
      const label = `${acceptedSpecs[0]?.sourceSystem ?? 'flat-file'} — ${rtypes.join(', ')}`;
      return api.saveFlatFileRecipe(sessionId, {
        headers: activeHeaders,
        specs: acceptedSpecs.map((s) => s.spec),
        authoredBy: 'human',
        label,
        uploadId: activeUploadId ?? undefined, // health-check at author time (no waiting)
      });
    },
    onSuccess: (res) => {
      // Active immediately — no review gate; the health check is the gate.
      const v = res.health ? ` · health ${res.health.verdict}` : '';
      toast.success(`Recipe v${res.version} active — reused on the next matching drop${v}`);
      void qc.invalidateQueries({ queryKey: lookupKey });
      if (ehrId) void qc.invalidateQueries({ queryKey: ['flat-file-recipes', ehrId] });
    },
    onError: (e) => toast.error(`Save failed: ${e instanceof Error ? e.message : String(e)}`),
  });

  const box = 'mb-3 rounded-xl px-3 py-2 text-xs flex items-center gap-2 flex-wrap';

  // ── No EHR attached: offer the one-field attach ──────────────────────────────
  if (!ehrId) {
    return (
      <div className={box} style={{ background: 'var(--bg-primary)', border: '1px dashed var(--border)' }}>
        <span style={{ color: 'var(--text-dimmed)' }}>⚗ No source EHR — attach one to reuse recipes across drops:</span>
        <input
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && slug.trim()) attach.mutate(); }}
          placeholder="ehr slug (e.g. athena)"
          className="rounded-lg px-2 py-1 text-xs outline-none font-mono"
          style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-primary)' }}
        />
        <button
          type="button"
          onClick={() => attach.mutate()}
          disabled={!slug.trim() || attach.isPending}
          className="rounded-lg px-2.5 py-1 text-[11px] font-semibold text-white disabled:opacity-40"
          style={{ background: 'linear-gradient(135deg, #0dcfb8 0%, #06b6d4 100%)' }}
        >
          {attach.isPending ? 'Attaching…' : 'Attach EHR'}
        </button>
      </div>
    );
  }

  if (!hasHeaders) return null;
  if (lookup.isLoading) {
    return (
      <div className={box} style={{ background: 'var(--bg-primary)', border: '1px solid var(--border)', color: 'var(--text-dimmed)' }}>
        ⚗ Checking for a reusable recipe…
      </div>
    );
  }

  const reuse = lookup.data?.reuse;
  const recipe = lookup.data?.recipe;
  const alternatives = lookup.data?.alternatives ?? [];

  // ── REUSE: an accepted recipe matches this exact shape ───────────────────────
  if (reuse && recipe) {
    const rtypes = [...new Set(recipe.specs.map((s) => (typeof s.resourceType === 'string' ? s.resourceType : '?')))];
    return (
      <div className={box} style={{ background: 'rgba(13,207,184,0.1)', border: '1px solid rgba(13,207,184,0.3)' }}>
        <span className="font-semibold" style={{ color: 'var(--accent-green)' }}>♻ Reusing recipe ✓</span>
        <span style={{ color: 'var(--text-secondary)' }}>
          {recipe.label || recipe.sourceSystem} · v{recipe.version} · {rtypes.join(', ')}
        </span>
        <span style={{ color: 'var(--text-dimmed)' }}>deterministic — no AI</span>
        {health.isFetching && <span style={{ color: 'var(--text-dimmed)' }}>· checking health…</span>}
        {health.data && (
          <span
            className="font-semibold"
            title={`coverage ${Math.round(health.data.health.headerCoverage * 100)}% · pass ${Math.round(health.data.health.passRate * 100)}% · refs ${Math.round(health.data.health.refRate * 100)}% (n=${health.data.health.sampleSize})`}
            style={{ color: health.data.health.verdict === 'PASS' ? 'var(--accent-green)' : 'var(--accent-red)' }}
          >
            {health.data.health.verdict === 'PASS'
              ? `· health ✓ ${Math.round(health.data.health.passRate * 100)}% valid`
              : `· health ✗ ${health.data.health.missingColumns.length ? `missing ${health.data.health.missingColumns.join(', ')}` : `${Math.round(health.data.health.passRate * 100)}% valid`}`}
          </span>
        )}
        <div className="ml-auto flex items-center gap-1.5">
          {health.data?.health.verdict === 'FAIL' && (
            <button
              type="button"
              onClick={() => rescope.mutate()}
              disabled={rescope.isPending || !activeUploadId}
              title="Re-map this recipe from the current file (fixes drift), then re-check"
              className="rounded-lg px-2.5 py-1 text-[11px] font-semibold text-white disabled:opacity-40"
              style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)' }}
            >
              {rescope.isPending ? 'Re-scoping…' : 'Re-scope'}
            </button>
          )}
          <button
            type="button"
            onClick={() => apply.mutate()}
            disabled={apply.isPending}
            className="rounded-lg px-2.5 py-1 text-[11px] font-semibold text-white disabled:opacity-40"
            style={{ background: 'linear-gradient(135deg, #0dcfb8 0%, #06b6d4 100%)' }}
          >
            {apply.isPending ? 'Applying…' : `Apply recipe (${recipe.specs.length} spec${recipe.specs.length !== 1 ? 's' : ''})`}
          </button>
        </div>
      </div>
    );
  }

  // ── NEW SHAPE: no match — author below; save when specs are accepted ──────────
  const canSave = acceptedSpecs.length > 0;
  return (
    <div className={box} style={{ background: alternatives.length ? 'rgba(255,209,102,0.08)' : 'var(--bg-primary)', border: `1px solid ${alternatives.length ? 'rgba(255,209,102,0.25)' : 'var(--border)'}` }}>
      <span style={{ color: alternatives.length ? 'var(--accent-yellow)' : 'var(--text-dimmed)' }}>
        {alternatives.length
          ? `⚠ New file shape — ${alternatives.length} other accepted shape${alternatives.length !== 1 ? 's' : ''} on file for this EHR (possible drift)`
          : '⚗ New file shape — author a mapping below, then save it as a reusable recipe'}
      </span>
      <button
        type="button"
        onClick={() => save.mutate()}
        disabled={!canSave || save.isPending}
        title={canSave ? 'Save the accepted specs as a reusable recipe for this file shape' : 'Accept at least one mapping spec first'}
        className="ml-auto rounded-lg px-2.5 py-1 text-[11px] font-semibold text-white disabled:opacity-40"
        style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)' }}
      >
        {save.isPending ? 'Saving…' : `Save as recipe${canSave ? ` (${acceptedSpecs.length})` : ''}`}
      </button>
    </div>
  );
}
